import { resolveWithBody, resolveNoContent } from "./Utils/utils";
import { usePromisedRequest as promiseRequest } from "../Common/request";

export type CouponUsageType = "personal" | "shared";

export type Coupon = {
    id: number;
    code: string;
    discountPercentage: number;
    maxUses: number | null;
    usageType: CouponUsageType;
    validUntil: string | null;
    active: boolean;
    created: string;
    modified: string;
};

export type CreateCoupon = {
    code: string;
    discountPercentage: number;
    maxUses: number | null;
    usageType: CouponUsageType;
    validUntil: string | null;
    active: boolean;
};

export type UpdateCoupon = {
    id: number;
    code: string;
    discountPercentage: number;
    maxUses: number | null;
    validUntil: string | null;
    active: boolean;
};

export type CouponUsageDetails = {
    id: number;
    userId: number;
    firstName: string;
    lastName: string;
    email: string;
    couponCode: string;
    originalAmount: string;
    discountPercentage: number;
    finalAmount: string;
    created: string;
};

export type CouponWithUsageCount = Coupon & {
    usedCount: number;
};

export default {
    getListing(eventId: number): Promise<CouponWithUsageCount[]> {
        return promiseRequest<CouponWithUsageCount[]>({
            url: `/api/admin/events/${eventId}/coupons`,
            method: "GET",
            json: true,
        }).then(resolveWithBody());
    },

    createCoupon(eventId: number, request: CreateCoupon): Promise<void> {
        return promiseRequest<void>({
            url: `/api/admin/events/${eventId}/coupons`,
            method: "POST",
            json: {
                eventId: eventId,
                ...request,
            },
        }).then(resolveNoContent());
    },

    updateCoupon(eventId: number, couponId: number, request: UpdateCoupon): Promise<void> {
        return promiseRequest<void>({
            url: `/api/admin/events/${eventId}/coupons/${couponId}`,
            method: "PUT",
            json: request,
        }).then(resolveNoContent());
    },

    deleteCoupon(eventId: number, couponId: number): Promise<void> {
        return promiseRequest<void>({
            url: `/api/admin/events/${eventId}/coupons/${couponId}`,
            method: "DELETE",
            json: true,
        }).then(resolveNoContent());
    },

    getUsageDetails(eventId: number, couponId: number): Promise<CouponUsageDetails[]> {
        return promiseRequest<CouponUsageDetails[]>({
            url: `/api/admin/events/${eventId}/coupons/${couponId}/usage`,
            method: "GET",
            json: true,
        }).then(resolveWithBody());
    },

    validateCoupon(eventId: number, userId: number, code: string): Promise<{ valid: boolean; coupon?: Coupon; message?: string }> {
        return promiseRequest<{ valid: boolean; coupon?: Coupon; message?: string }>({
            url: `/api/users/${userId}/events/${eventId}/coupons/validate`,
            method: "POST",
            json: { code },
        }).then(resolveWithBody());
    },
};
